import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Alert,
  Button,
  IconButton,
  Card,
  CardContent,
  Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import apiClient from '../../services/api';

const RefereeForm = ({ initialData = [], onSave, onCancel, loading = false }) => {
  const [refereeList, setRefereeList] = React.useState(
    initialData.length > 0 ? initialData : [{}]
  );
  const [errors, setErrors] = React.useState({});
  const [saveError, setSaveError] = React.useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const [isDataModified, setIsDataModified] = React.useState(false);

  // 监听initialData变化，更新表单数据
  React.useEffect(() => {
    if (!isDataModified) {
      setRefereeList(initialData.length > 0 ? initialData : [{}]);
    }
  }, [initialData, isDataModified]);

  // 监听loading状态，触发保存
  React.useEffect(() => {
    if (loading) {
      setTimeout(() => {
        handleSave();
      }, 100);
    }
  }, [loading]);

  // 处理表单字段变化
  const handleFormChange = (index, field, value) => {
    setRefereeList(prev => {
      const newList = [...prev];
      newList[index] = {
        ...newList[index],
        [field]: value
      };
      return newList;
    });
    
    // 清除对应字段的错误
    const errorKey = `referee.${index}.${field}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
    
    setIsDataModified(true);
  };

  // 添加推荐人
  const handleAddReferee = () => {
    if (refereeList.length < 2) {
      setRefereeList(prev => [...prev, {}]);
      setIsDataModified(true);
    }
  };

  // 删除推荐人
  const handleRemoveReferee = (index) => {
    if (refereeList.length > 1) {
      setRefereeList(prev => prev.filter((_, i) => i !== index));
      
      // 清除相关错误
      setErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith(`referee.${index}.`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
      
      setIsDataModified(true);
    }
  };

  // 验证表单
  const validateForm = () => {
    const newErrors = {};
    
    refereeList.forEach((referee, index) => {
      if (!referee.name) {
        newErrors[`referee.${index}.name`] = '请输入推荐人姓名';
      }
      if (!referee.position) {
        newErrors[`referee.${index}.position`] = '请输入推荐人职位';
      }
      if (!referee.companyName) {
        newErrors[`referee.${index}.companyName`] = '请输入工作单位名称';
      }
      if (!referee.phone && !referee.mobile && !referee.email) {
        newErrors[`referee.${index}.phone`] = '请至少填写一个联系方式';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 处理保存
  const handleSave = async () => {
    setSaveError('');
    
    if (!validateForm()) {
      onSave && onSave(null);
      setShowErrorSnackbar(true);
      return;
    }

    try {
      const response = await apiClient.post('/api/application/update-referee', {
        referee: refereeList
      });
      
      if (response && response.success) {
        setIsDataModified(false);
        onSave && onSave(refereeList);
      } else {
        setSaveError(response?.message || '保存失败');
        onSave && onSave(null);
        setShowErrorSnackbar(true);
      }
    } catch (error) {
      console.error('保存推荐人信息失败:', error);
      setSaveError(error?.response?.data?.message || error.message || '保存失败');
      onSave && onSave(null);
      setShowErrorSnackbar(true);
    }
  };

  return (
    <Box>
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowErrorSnackbar(false)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {saveError}
        </Alert>
      </Snackbar>
      
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
        推荐人信息
      </Typography>

      {refereeList.map((referee, index) => (
        <Card key={index} sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                推荐人 {index + 1}
              </Typography>
              {refereeList.length > 1 && (
                <IconButton
                  color="error"
                  onClick={() => handleRemoveReferee(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="姓名 *"
                  value={referee.name || ''}
                  onChange={(e) => handleFormChange(index, 'name', e.target.value)}
                  error={!!errors[`referee.${index}.name`]}
                  helperText={errors[`referee.${index}.name`]}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="职位 *"
                  value={referee.position || ''}
                  onChange={(e) => handleFormChange(index, 'position', e.target.value)}
                  error={!!errors[`referee.${index}.position`]}
                  helperText={errors[`referee.${index}.position`]}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="工作单位名称 *"
                  value={referee.companyName || ''}
                  onChange={(e) => handleFormChange(index, 'companyName', e.target.value)}
                  error={!!errors[`referee.${index}.companyName`]}
                  helperText={errors[`referee.${index}.companyName`]}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="手机号码"
                  value={referee.phone || referee.mobile || ''}
                  onChange={(e) => handleFormChange(index, 'phone', e.target.value)}
                  error={!!errors[`referee.${index}.phone`]}
                  helperText={errors[`referee.${index}.phone`]}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="工作电话"
                  value={referee.workPhone || ''}
                  onChange={(e) => handleFormChange(index, 'workPhone', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="邮箱"
                  type="email"
                  value={referee.email || ''}
                  onChange={(e) => handleFormChange(index, 'email', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="传真"
                  value={referee.fax || ''}
                  onChange={(e) => handleFormChange(index, 'fax', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="网站"
                  value={referee.website || ''}
                  onChange={(e) => handleFormChange(index, 'website', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="公司地址"
                  multiline
                  rows={2}
                  value={referee.companyAddress || referee.address || ''}
                  onChange={(e) => handleFormChange(index, 'companyAddress', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="邮政编码"
                  value={referee.postalCode || ''}
                  onChange={(e) => handleFormChange(index, 'postalCode', e.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
      
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddReferee}
        disabled={refereeList.length >= 2}
        sx={{ mb: 3 }}
      >
        添加推荐人 {refereeList.length}/2
      </Button>
    </Box>
  );
};

export default RefereeForm; 